import { useEffect, useState } from "react";
import { LuLoaderCircle } from "react-icons/lu";
import { typography, components } from "../../../design/tokens";
import {
  ExportStatus,
  useProjectExportsActions,
  useProjectExportStatus,
  useProjectLatestExport,
} from "../../../core_stores/useProjectExportsStore";
import { HttpNotEnoughTokensException } from "../../../model/httpException";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import { useSubscriptionsModal } from "../../../providers/SubscriptionsModalProvider";
import BuildProgressBar from "../../common_components/BuildProgressBar";

export default function ProjectExportStartExport({
  canExport,
}: {
  canExport: boolean;
}) {
  const latestExport = useProjectLatestExport();
  const exportStatus = useProjectExportStatus();

  const { startExport } = useProjectExportsActions();
  const { showSnackbar } = useSnackbar();
  const { open: openSubscriptionsModal } = useSubscriptionsModal();

  const [isLoading, setIsLoading] = useState(false);

  const isExporting = exportStatus === ExportStatus.EXPORTING;

  useEffect(() => {
    if (isExporting && !isLoading) {
      setIsLoading(true);
    } else if (!isExporting && isLoading) {
      setIsLoading(false);
    }
  }, [isExporting]);

  const handleClick = async () => {
    setIsLoading(true);
    try {
      await startExport();
    } catch (error) {
      if (error instanceof HttpNotEnoughTokensException) {
        showSnackbar("You don't have enough tokens to export your project.", {
          type: "error",
        });
        setIsLoading(false);
      }
    }
  };

  const getDescription = () => {
    if (isLoading) {
      return "Export in progress\nEstimated time 1-2 minutes.\nYou can return here when it's ready.";
    }
    return "Export project";
  };

  return (
    <div
      className="flex flex-col items-center justify-center
      border rounded-xl border-dark-border
      py-[50px] mr-2 gap-4 relative"
    >
      <span className={`max-w-[300px] text-center whitespace-pre-line ${typography.size.base} ${typography.weight.normal}`}>
        {getDescription()}
      </span>
      {isExporting ? (
        <BuildProgressBar
          buildCreationTimestamp={latestExport?.creationTimestamp}
          estimatedBuildDurationInSeconds={120}
        />
      ) : (
        <Button
          label={"Export"}
          isLoading={isLoading}
          isDisabled={isLoading || !canExport}
          handleClick={handleClick}
        />
      )}
      {!canExport && (
        <p className={`max-w-[500px] px-4 text-center ${typography.size.sm} text-dark-text-secondary mt-2`}>
          Project exports are available only to active paid subscribers.
          <br />
          <button
            id="export-project-upgrade-plan-btn"
            onClick={openSubscriptionsModal}
            className="text-white hover:brightness-200 hover:underline transition-all duration-300 cursor-pointer"
          >
            Upgrade your plan
          </button>{" "}
          to export your project.
        </p>
      )}
    </div>
  );
}

function Button({
  label,
  isLoading,
  isDisabled,
  handleClick,
}: {
  label: string;
  isLoading: boolean;
  isDisabled: boolean;
  handleClick: () => void;
}) {
  return (
    <button
      id="export-project-start-export"
      className={`${components.button.primary} w-full max-w-[250px] overflow-hidden`}
      onClick={handleClick}
      disabled={isDisabled}
    >
      {isLoading ? (
        <LuLoaderCircle className="animate-spin" size={20} />
      ) : (
        label
      )}
    </button>
  );
}
